import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Slider,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  FavoriteBorder as FavoriteIcon,
  VerifiedUser as VerifiedIcon,
  Lock as LockIcon,
  Group as GroupIcon,
  Favorite as TrustIcon,
  LocationOn as LocationIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import api from '../services/api';
import { toast } from 'react-toastify';
import RotatingProfileCard from '../components/RotatingProfileCard';
import '../styles/maandvo-home.css';

interface HomeProfile {
  id: number;
  profile_id?: string;
  name: string;
  gender?: string;
  date_of_birth?: string;
  age: number;
  height: string;
  location: string;
  country?: string;
  marital_status?: string;
  work_location?: string;
  religion: string;
  caste?: string;
  education: string;
  occupation: string;
  income?: string;
  photos?: Array<{ id: number }>;
  matchScore: number;
}

interface PublicFilterConfig {
  field_key: string;
  label: string;
  input_type: string;
  options: string[];
  depends_on?: string | null;
  grouped_options?: Record<string, string[]>;
  is_enabled: boolean;
  is_advanced?: boolean;
}

const CARD_GRADIENT = 'linear-gradient(135deg, #fff7ec 0%, #fffdf8 100%)';
const MAROON = '#7b0011';
const HOME_SEARCH_STATE_KEY = 'maandvo-home-search-state';

type HomeSearchState = {
  ageRange: number[];
  heightRange: number[];
  selectedFilters: { gender: string; maritalStatus: string; currentCountry: string; community: string };
  advancedFilters: Record<string, string>;
  hasSearched: boolean;
  randomSeed: string;
};

const getSavedHomeSearch = (): HomeSearchState | null => {
  try {
    const raw = sessionStorage.getItem(HOME_SEARCH_STATE_KEY);
    return raw ? JSON.parse(raw) as HomeSearchState : null;
  } catch {
    return null;
  }
};

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const mediaOrigin = String(api.defaults.baseURL || '').replace(/\/?api\/?$/, '');
  const publicBrowseBaseUrl = `${String(api.defaults.baseURL || '').replace(/\/?$/, '')}/profiles/browse`;
  const pageSize = 12;
  const savedSearch = getSavedHomeSearch();

  const [ageRange, setAgeRange] = useState(savedSearch?.ageRange || [18, 80]);
  const [heightRange, setHeightRange] = useState(savedSearch?.heightRange || [140, 200]);
  const [selectedFilters, setSelectedFilters] = useState(savedSearch?.selectedFilters || {
    gender: '',
    maritalStatus: '',
    currentCountry: '',
    community: '',
  });
  const [filterOptions, setFilterOptions] = useState<Record<string, PublicFilterConfig>>({});
  const [filteredProfiles, setFilteredProfiles] = useState<HomeProfile[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingProfiles, setLoadingProfiles] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [randomSeed, setRandomSeed] = useState(savedSearch?.randomSeed || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasSearched, setHasSearched] = useState(savedSearch?.hasSearched || false);
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState<Record<string, string>>(savedSearch?.advancedFilters || {});

  const mapApiProfile = (profile: any): HomeProfile => ({
    id: profile.id,
    profile_id: profile.profile_id,
    name: `${profile.first_name} ${profile.last_name}`.trim(),
    gender: profile.gender || '',
    date_of_birth: profile.date_of_birth,
    age: profile.age,
    height: String(profile.height_cm || ''),
    location: [profile.city, profile.state].filter(Boolean).join(', '),
    country: profile.country || '',
    marital_status: profile.marital_status || '',
    work_location: profile.work_location || '',
    religion: profile.religion || '',
    caste: profile.caste || '',
    education: profile.education || '',
    occupation: profile.occupation || '',
    income: profile.annual_income || '',
    photos: profile.photos || [],
    matchScore: profile.match_score || 75,
  });

  const fetchHomeProfiles = async (cursorToken: string | null, append: boolean, orderingSeed = randomSeed) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoadingProfiles(true);
      }

      const params = new URLSearchParams();
      const requestedPage = append ? Math.floor(filteredProfiles.length / pageSize) + 1 : 1;
      if (!cursorToken) {
        params.append('page', String(requestedPage));
      } else {
        params.append('cursor', cursorToken);
        params.append('page', String(requestedPage));
      }
      params.append('page_size', String(pageSize));
      params.append('random_seed', orderingSeed);
      params.append('age_min', String(ageRange[0]));
      params.append('age_max', String(ageRange[1]));
      params.append('height_min', String(heightRange[0]));
      params.append('height_max', String(heightRange[1]));

      if (searchQuery.trim()) params.append('q', searchQuery.trim());
      if (selectedFilters.gender) params.append('gender', selectedFilters.gender);
      if (selectedFilters.maritalStatus) params.append('marital_status', selectedFilters.maritalStatus);
      if (selectedFilters.currentCountry) params.append('current_country', selectedFilters.currentCountry);
      if (selectedFilters.community) params.append('sub_caste', selectedFilters.community);
      Object.entries(advancedFilters).forEach(([key, value]) => {
        if (value.trim()) params.append(key, value);
      });

      const response = await fetch(`${publicBrowseBaseUrl}?${params.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('accessToken') ? { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } : {}),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load public profiles (${response.status})`);
      }

      const payload = await response.json();
      const liveProfiles: HomeProfile[] = (payload.profiles || []).map(mapApiProfile);

      setFilteredProfiles((prev) => (append ? [...prev, ...liveProfiles] : liveProfiles));
      setTotalCount(payload.total_count || 0);
      setHasNext(Boolean(payload.has_next));
      setNextCursor(payload.next_cursor || null);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load profiles on home page');
    } finally {
      setLoadingProfiles(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchHomeProfiles(null, false);
  }, []);

  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const response = await api.get<{ items: PublicFilterConfig[] }>('/profiles/filter-config');
        setFilterOptions(Object.fromEntries((response.data.items || []).filter((item) => item.is_enabled).map((item) => [item.field_key, item])));
      } catch (error) {
        console.error('Failed to load home search options', error);
      }
    };
    void loadFilterOptions();
  }, []);

  const handleFilterChange = (filterName: string, value: string) => {
    setSelectedFilters((prev) => ({ ...prev, [filterName]: value }));
  };

  const ageOptions = [
    { label: '18 - 25', value: '18-25', range: [18, 25] },
    { label: '26 - 30', value: '26-30', range: [26, 30] },
    { label: '31 - 35', value: '31-35', range: [31, 35] },
    { label: '36 - 45', value: '36-45', range: [36, 45] },
    { label: '46 - 60', value: '46-60', range: [46, 60] },
    { label: '60+', value: '60-80', range: [60, 80] },
  ];
  const selectedAgeOption = ageOptions.find((option) => option.range[0] === ageRange[0] && option.range[1] === ageRange[1])?.value || '';
  const countryConfig = filterOptions.current_country;
  const genderOptions = filterOptions.gender?.options || [];
  const countryOptions = countryConfig?.options || [];
  const communityConfig = filterOptions.sub_caste;
  const communityOptions = Array.from(new Set([
    ...(communityConfig?.options || []),
    ...Object.values(communityConfig?.grouped_options || {}).flat(),
  ]));
  const maritalOptions = filterOptions.marital_status?.options || ['NEVER_MARRIED', 'DIVORCED', 'WIDOWED', 'SEPARATED'];
  const primaryFilterKeys = new Set(['gender', 'marital_status', 'current_country', 'current_city', 'sub_caste']);
  const advancedFilterConfigs = Object.values(filterOptions).filter((config) => config.is_enabled && (config as PublicFilterConfig & { is_advanced?: boolean }).is_advanced && !primaryFilterKeys.has(config.field_key));

  const getAdvancedOptions = (config: PublicFilterConfig) => {
    if (config.depends_on) {
      const parentValue = advancedFilters[config.depends_on] || selectedFilters[config.depends_on as keyof typeof selectedFilters] || '';
      const grouped = config.grouped_options || {};
      const matchingKey = Object.keys(grouped).find((key) => key.toLowerCase() === parentValue.toLowerCase());
      return matchingKey ? grouped[matchingKey] : [];
    }
    return config.options || [];
  };

  const handleViewProfile = (profileId: number | string) => {
    sessionStorage.setItem(HOME_SEARCH_STATE_KEY, JSON.stringify({ ageRange, heightRange, selectedFilters, advancedFilters, hasSearched: true, randomSeed } satisfies HomeSearchState));
    if (isAuthenticated) {
      navigate(`/profile/${profileId}`, { state: { from: '/' } });
    } else {
      navigate('/login', { state: { from: '/' } });
    }
  };

  const handleLoadMore = () => {
    if (!isAuthenticated) {
      toast.info('Please log in to view more profiles');
      navigate('/login', { state: { from: '/' } });
      return;
    }
    if (nextCursor) void fetchHomeProfiles(nextCursor, true);
  };

  const trustItems = useMemo(
    () => [
      { icon: <VerifiedIcon />, label: 'Verified Profiles' },
      { icon: <LockIcon />, label: '100% Secure' },
      { icon: <GroupIcon />, label: 'Personalized Matchmaking' },
      { icon: <TrustIcon />, label: 'Trusted by Families' },
    ],
    []
  );

  return (
    <Box className="maandvo-home">
      <Box className="maandvo-card">
      <Box className="maandvo-shell">
        <Box className="maandvo-hero">
          <Box
            component="img"
            src="/maandvo-hero-banner.jpg"
            alt="Maandvo Matrimony Service - Bringing Hearts Together Since 2020"
            className="maandvo-hero-banner"
          />

          <Box className="maandvo-search-card">
            <Box className="maandvo-search-row">
              {/* I am looking for (Gender) */}
              <Box className="maandvo-field">
                <Box className="maandvo-field-icon">👤</Box>
                <Box className="maandvo-field-content">
                  <span className="maandvo-field-label">I am looking for</span>
                  <Select
                    value={selectedFilters.gender}
                    onChange={(e) => handleFilterChange('gender', String(e.target.value))}
                    variant="standard"
                    disableUnderline
                    displayEmpty
                    fullWidth
                  >
                    <MenuItem value="">Select</MenuItem>
                    {genderOptions.map((option) => <MenuItem key={option} value={option}>{option.replace(/_/g, ' ')}</MenuItem>)}
                  </Select>
                </Box>
              </Box>

              {/* Age */}
              <Box className="maandvo-field">
                <Box className="maandvo-field-icon">📅</Box>
                <Box className="maandvo-field-content">
                  <span className="maandvo-field-label">Age</span>
                  <Select value={selectedAgeOption} onChange={(e) => { const selectedValue = String(e.target.value); const range = ageOptions.find((option) => option.value === selectedValue)?.range; setAgeRange(range ? [...range] : [18, 80]); }} variant="standard" disableUnderline displayEmpty fullWidth>
                    <MenuItem value="">Select Age</MenuItem>
                    {ageOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                  </Select>
                </Box>
              </Box>

              {/* Marital Status */}
              <Box className="maandvo-field">
                <Box className="maandvo-field-icon">💍</Box>
                <Box className="maandvo-field-content">
                  <span className="maandvo-field-label">Marital Status</span>
                  <Select
                    value={selectedFilters.maritalStatus}
                    onChange={(e) => handleFilterChange('maritalStatus', String(e.target.value))}
                    variant="standard"
                    disableUnderline
                    displayEmpty
                    fullWidth
                  >
                    <MenuItem value="">Select</MenuItem>
                    {maritalOptions.map((status) => <MenuItem key={status} value={status}>{status.replace(/_/g, ' ')}</MenuItem>)}
                  </Select>
                </Box>
              </Box>

              {/* Current Country */}
              <Box className="maandvo-field">
                <Box className="maandvo-field-icon"><LocationIcon fontSize="small" /></Box>
                <Box className="maandvo-field-content">
                  <span className="maandvo-field-label">Country</span>
                  <Select
                    value={selectedFilters.currentCountry}
                    onChange={(e) => handleFilterChange('currentCountry', String(e.target.value))}
                    variant="standard"
                    disableUnderline
                    displayEmpty
                    fullWidth
                  >
                    <MenuItem value="">Select</MenuItem>
                    {countryOptions.length ? countryOptions.map((country) => <MenuItem key={country} value={country}>{country}</MenuItem>) : <MenuItem disabled value="">No countries configured</MenuItem>}
                  </Select>
                </Box>
              </Box>

              {/* Community */}
              <Box className="maandvo-field">
                <Box className="maandvo-field-icon"><GroupIcon fontSize="small" /></Box>
                <Box className="maandvo-field-content">
                  <span className="maandvo-field-label">Community</span>
                  <Select
                    value={selectedFilters.community}
                    onChange={(e) => handleFilterChange('community', String(e.target.value))}
                    variant="standard"
                    disableUnderline
                    displayEmpty
                    fullWidth
                  >
                    <MenuItem value="">Select</MenuItem>
                    {communityOptions.length ? communityOptions.map((community) => <MenuItem key={community} value={community}>{community}</MenuItem>) : <MenuItem disabled value="">No communities configured</MenuItem>}
                  </Select>
                </Box>
              </Box>

              <Box className="maandvo-search-actions">
                <Button
                  variant="contained"
                  className="maandvo-search-button"
                  startIcon={<SearchIcon fontSize="small" />}
                  onClick={() => {
                    setHasSearched(true);
                    const orderingSeed = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
                    setRandomSeed(orderingSeed);
                    sessionStorage.setItem(HOME_SEARCH_STATE_KEY, JSON.stringify({ ageRange, heightRange, selectedFilters, advancedFilters, hasSearched: true, randomSeed: orderingSeed } satisfies HomeSearchState));
                    fetchHomeProfiles(null, false, orderingSeed);
                  }}
                >
                  Search
                </Button>
                {advancedFilterConfigs.length > 0 && (
                  <Button
                    size="small"
                    variant="outlined"
                    className="maandvo-more-filters-button"
                    onClick={() => setAdvancedFiltersOpen((open) => !open)}
                  >
                    {advancedFiltersOpen ? 'Hide Filters' : 'More Filters'}
                  </Button>
                )}
              </Box>
            </Box>

              {advancedFilterConfigs.length > 0 && (
                <Box sx={{ mt: 1.25 }}>
                  {advancedFiltersOpen && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.1, mt: 0.75, p: 1.25, border: '1px solid #ead7be', borderRadius: 2, backgroundColor: '#fffaf2' }}>
                      {advancedFilterConfigs.map((config) => {
                        const options = getAdvancedOptions(config);
                        const parentValue = config.depends_on ? advancedFilters[config.depends_on] || selectedFilters[config.depends_on as keyof typeof selectedFilters] || '' : '';
                        return config.input_type === 'select' || options.length > 0 ? (
                          <TextField
                            key={config.field_key}
                            select
                            size="small"
                            label={config.label}
                            value={advancedFilters[config.field_key] || ''}
                            disabled={Boolean(config.depends_on && !parentValue)}
                            onChange={(event) => setAdvancedFilters((previous) => ({ ...previous, [config.field_key]: event.target.value }))}
                            sx={{ backgroundColor: '#fffdf9', '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                          >
                            <MenuItem value="">All</MenuItem>
                            {options.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                          </TextField>
                        ) : (
                          <TextField
                            key={config.field_key}
                            size="small"
                            label={config.label}
                            value={advancedFilters[config.field_key] || ''}
                            onChange={(event) => setAdvancedFilters((previous) => ({ ...previous, [config.field_key]: event.target.value }))}
                            sx={{ backgroundColor: '#fffdf9', '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                          />
                        );
                      })}
                    </Box>
                  )}
                </Box>
              )}

            <Box className="maandvo-range-row">
              <Box style={{ flex: 1 }}>
                <Typography variant="caption" sx={{ color: MAROON, fontWeight: 800 }}>
                  Age: {ageRange[0]} - {ageRange[1]}
                </Typography>
                <Slider value={ageRange} min={18} max={80} onChange={(_, v) => setAgeRange(v as number[])} sx={{ color: MAROON }} />
              </Box>
              <Box style={{ flex: 1 }}>
                <Typography variant="caption" sx={{ color: MAROON, fontWeight: 800 }}>
                  Height: {heightRange[0]} - {heightRange[1]} cm
                </Typography>
                <Slider value={heightRange} min={140} max={200} onChange={(_, v) => setHeightRange(v as number[])} sx={{ color: '#8b1c31' }} />
              </Box>
              <Button
                variant="outlined"
                sx={{ borderColor: MAROON, color: MAROON, fontFamily: "'Georgia', 'Times New Roman', serif" }}
                onClick={() => {
                  setSearchQuery('');
                  setAgeRange([18, 80]);
                  setHeightRange([140, 200]);
                  setSelectedFilters({ gender: '', maritalStatus: '', currentCountry: '', community: '' });
                  setAdvancedFilters({});
                  sessionStorage.removeItem(HOME_SEARCH_STATE_KEY);
                  setRandomSeed(`${Date.now()}-${Math.random().toString(36).slice(2)}`);
                  setHasSearched(false);
                  fetchHomeProfiles(null, false);
                }}
              >
                Clear
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ===== TRUST CALLOUTS ===== */}
      <Box className="maandvo-trust">
        <Box className="maandvo-trust-grid">
          {trustItems.map((item) => (
            <Box key={item.label} className="maandvo-trust-item">
              <Box className="maandvo-trust-icon">{item.icon}</Box>
              <Typography className="maandvo-trust-label">{item.label}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {hasSearched && (
        <Container maxWidth="lg" sx={{ py: 6 }}>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: MAROON }}>
              Search Results ({totalCount})
            </Typography>
          </Box>

          {loadingProfiles ? (
            <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress sx={{ color: MAROON }} />
            </Box>
          ) : filteredProfiles.length === 0 ? (
            <Alert severity="info">No profiles found. Try adjusting your preferences.</Alert>
          ) : (
            <>
              <Grid container spacing={2} sx={{ mb: 4 }}>
                {filteredProfiles.map((profile) => (
                  <Grid item xs={6} sm={4} md={3} lg={2.4} key={profile.id}>
                    <RotatingProfileCard
                      profile={{
                        id: profile.id,
                        profile_id: profile.profile_id,
                        first_name: profile.name.split(' ')[0] || profile.name,
                        last_name: profile.name.split(' ').slice(1).join(' '),
                        gender: profile.gender,
                        date_of_birth: profile.date_of_birth,
                        age: profile.age,
                        height_cm: Number(profile.height),
                        city: profile.location.split(',')[0] || '',
                        state: profile.location.split(',')[1]?.trim() || '',
                        country: profile.country,
                        caste: profile.caste,
                        marital_status: profile.marital_status,
                        work_location: profile.work_location,
                        education: profile.education,
                        occupation: profile.occupation,
                        religion: profile.religion,
                        photos: profile.photos,
                      }}
                      mediaOrigin={mediaOrigin}
                      onView={() => handleViewProfile(profile.profile_id || profile.id)}
                      primaryActionLabel="View Profile"
                      displayReligion
                      compact
                    />
                  </Grid>
                ))}
              </Grid>

              {hasNext && nextCursor && (
                <Box sx={{ textAlign: 'center' }}>
                  <Button variant="outlined" disabled={loadingMore} onClick={handleLoadMore} sx={{ borderColor: MAROON, color: MAROON }}>
                    {loadingMore ? 'Loading...' : isAuthenticated ? 'Load More' : 'Log in to view more profiles'}
                  </Button>
                </Box>
              )}
            </>
          )}
        </Container>
      )}

      <Box sx={{ background: '#fffaf2', borderTop: '1px solid #eddac2', borderBottom: '1px solid #eddac2', py: { xs: 5, md: 7 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={3} alignItems="stretch">
            <Grid item xs={12} md={7}>
              <Typography variant="h4" sx={{ color: MAROON, fontWeight: 900, mb: 1 }}>About Maandvo Matrimony</Typography>
              <Typography sx={{ color: '#5d473e', lineHeight: 1.8 }}>With years of experience and a deep respect for Gujarati family values, we help families find meaningful, compatible matches through a trusted digital platform.</Typography>
              <Button onClick={() => navigate('/about-us')} sx={{ mt: 2, color: MAROON, fontWeight: 800 }}>Read About Us</Button>
            </Grid>
            <Grid item xs={12} md={5}>
              <Paper elevation={0} sx={{ p: 3, height: '100%', border: '1px solid #eddac2', borderRadius: 3, background: '#fdf3e4' }}>
                <Typography variant="h5" sx={{ color: MAROON, fontWeight: 800, mb: 1 }}>Need help?</Typography>
                <Typography sx={{ color: '#5d473e' }}>Speak with Kinnari Shah and Krishnakant Shah about your family&apos;s matrimonial journey.</Typography>
                <Typography sx={{ color: '#7a5a4d', mt: 1, fontWeight: 700 }}>Call Timings: Mon-Sat, 11:00 AM to 6:00 PM</Typography>
                <Typography sx={{ color: '#7a5a4d', mt: 0.3 }}>This number is also available on WhatsApp.</Typography>
                <Button variant="contained" onClick={() => navigate('/contact')} sx={{ mt: 2, background: MAROON, '&:hover': { background: '#5d000d' } }}>Contact Us</Button>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ===== FEATURES ===== */}
      <Box className="maandvo-features">
        <Box component="img" src="/maandvo-wave.jpg" alt="" className="maandvo-wave" />
        <Box component="img" src="/maandvo-floral-left.png" alt="" className="maandvo-floral-left" />
        <Box component="img" src="/maandvo-floral-right.png" alt="" className="maandvo-floral-right" />
        <Box className="maandvo-features-content">
          <Typography className="maandvo-features-title">Where Hearts Meet & Families Unite</Typography>
          <Box className="maandvo-features-divider" />
          <Box className="maandvo-features-grid">
            <Box className="maandvo-feature-item">
              <Box className="maandvo-feature-icon"><VerifiedIcon fontSize="small" /></Box>
              <Typography component="h3">Trusted Service</Typography>
              <Typography component="p">Built on trust and transparency</Typography>
            </Box>
            <Box className="maandvo-feature-item">
              <Box className="maandvo-feature-icon"><LockIcon fontSize="small" /></Box>
              <Typography component="h3">Safe & Secure</Typography>
              <Typography component="p">Your privacy is our priority</Typography>
            </Box>
            <Box className="maandvo-feature-item">
              <Box className="maandvo-feature-icon"><GroupIcon fontSize="small" /></Box>
              <Typography component="h3">Better Matches</Typography>
              <Typography component="p">Advanced matching for better connections</Typography>
            </Box>
            <Box className="maandvo-feature-item">
              <Box className="maandvo-feature-icon"><TrustIcon fontSize="small" /></Box>
              <Typography component="h3">Happy Families</Typography>
              <Typography component="p">Successful matches that last forever</Typography>
            </Box>
          </Box>
        </Box>
      </Box>
      </Box>

      {/* ===== FOOTER ===== */}
      <Box className="maandvo-footer">
        <Typography component="div">
          Your privacy and trust are our priority. 100% secure and confidential service.
        </Typography>
        <Button
          onClick={() => navigate('/terms-of-use')}
          sx={{ color: MAROON, mt: 1, fontWeight: 800, textTransform: 'none' }}
        >
          Terms of Use and Policies
        </Button>
        <Typography component="div" sx={{ mt: 1, color: '#7a5a4d', fontSize: '0.85rem' }}>
          © {new Date().getFullYear()} Maandvo Matrimony Service. All Rights Reserved
          <br />
          Managed by TechwithT
        </Typography>
      </Box>
    </Box>
  );
};

export default HomePage;
